import { useMemo } from "react";

import { Grid, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import { formatDate, formatNumber, formatPercentage, formatPrice } from "utility/formatters";

import { formatMaturity, getDoubleBehavior } from "../OTCHelper";

const KnockoutContent = ({ otcData, calculations, transactionData }) => {
  const { totalAccumulationEvents, accumulatedPeriods, remainingPeriods, perPeriodQty } = useMemo(
    () => ({
      totalAccumulationEvents: calculations.totalAccumulationEvents,
      accumulatedPeriods: calculations.accumulatedPeriods,
      remainingPeriods: calculations.remainingPeriods,
      perPeriodQty: calculations.perPeriodQty,
    }),
    [otcData]
  );

  const descriptionRows = [
    { label: "Structure type", content: `${otcData.type} – ${otcData.event_type}` },
    { label: "Trade ID #", content: otcData.tracer_number },
    { label: "Contract", content: `${formatMaturity(otcData.maturity)} ${otcData.commodity_name}` },
    {
      label: "Qty",
      content: `${formatNumber(Math.abs(otcData.original_quantity_in_units || 0), 0)} ${otcData.unit_type?.toLowerCase()}`,
    },
    { label: "Accumulation Events", content: totalAccumulationEvents },
    {
      label: "Accumulation Type",
      content: otcData.period,
    },
    {
      label: "Double Behavior",
      content: getDoubleBehavior(otcData.period, otcData.at_expiry),
    },
    { label: "Accumulation Start Date", content: formatDate(otcData.trade_date) },
    { label: "Accumulation End Date", content: formatDate(otcData.option_expire_date) },
    {
      label: "Floor Level",
      content: `${formatPrice(otcData.event_level, 2)} / ${otcData.unit_type?.toLowerCase()}`,
    },
    {
      label: "Enhancement Level",
      content: `${formatPrice(otcData.enhancement_level, 2)} / ${otcData.unit_type?.toLowerCase()}`,
    },
  ];

  const performanceRows = [
    {
      label: "Accumulated Periods",
      content: accumulatedPeriods,
      suffix: formatPercentage(accumulatedPeriods / (totalAccumulationEvents || 1)),
    },
    {
      label: "Remaining Periods",
      content: remainingPeriods,
      suffix: formatPercentage(remainingPeriods / (totalAccumulationEvents || 1)),
    },
    { spacer: true },
    {
      label: (otcData.period || "Per Period") + " Qty",
      content: `${formatNumber(perPeriodQty, 0)} ${otcData.unit_type?.toLowerCase()}`,
      suffix: "",
    },
    {
      label: "Accumulated volume:",
      content: `${formatNumber(
        Math.abs((otcData.quantity_accumulated || 0) * (otcData.multiplication_factor || 1)),
        0
      )}`,
      suffix: formatPercentage(
        Math.abs(otcData.quantity_accumulated * otcData.multiplication_factor || 0) /
          Math.abs(otcData.original_quantity_in_units || 1)
      ),
    },
    {
      label: "Remaining volume:",
      content: `${formatNumber(Math.abs(otcData.periods_remaining * perPeriodQty || 0), 0)}`,
      suffix: formatPercentage(
        Math.abs(otcData.periods_remaining * perPeriodQty || 0) / Math.abs(otcData.original_quantity_in_units || 1)
      ),
    },
    {
      label: "Max volume with double:",
      content: `${formatNumber(
        Math.abs(otcData.periods_remaining * perPeriodQty || 0) * 2 +
          Math.abs((otcData.quantity_accumulated || 0) * (otcData.multiplication_factor || 1)),
        0
      )} ${otcData.unit_type?.toLowerCase()}`,
      suffix: formatPercentage(
        (Math.abs(otcData.periods_remaining * perPeriodQty || 0) * 2 +
          Math.abs((otcData.quantity_accumulated || 0) * (otcData.multiplication_factor || 1))) /
          Math.abs(otcData.original_quantity_in_units || 1)
      ),
    },
    { spacer: true },
    {
      label: "Accumulated Price",
      content: formatPrice(otcData.enhancement_level, 2),
      suffix: "",
    },
    {
      label: "Market Average Price",
      content: formatPrice(transactionData?.accumulated_average_price, 2),
      suffix: "",
    },
  ];

  const renderRows = (rows) =>
    rows.map((row, i) =>
      row.spacer ? (
        <TableRow key={i}>
          <TableCell colSpan={3}>&nbsp;</TableCell>
        </TableRow>
      ) : (
        <TableRow key={i}>
          <TableCell component="th" scope="row" sx={{ width: row.width || "50%" }}>
            {row.label}
          </TableCell>
          <TableCell>{row.content}</TableCell>
          {row.suffix != null && (
            <TableCell align="right" sx={{ width: "20%" }}>
              {row.suffix}
            </TableCell>
          )}
        </TableRow>
      )
    );

  return (
    <>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ bgcolor: "colors.borderVeryLight", color: "text.primary", p: 1, mb: 2 }}>
          Description
        </Typography>
        <TableContainer>
          <Table size="small" className="reportTable">
            <TableBody>{renderRows(descriptionRows)}</TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ bgcolor: "colors.borderVeryLight", color: "text.primary", p: 1, mb: 2 }}>
          Performance
        </Typography>
        <TableContainer>
          <Table size="small" className="reportTable">
            <TableBody>{renderRows(performanceRows)}</TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
};

export default KnockoutContent;
